import React, { useState, useEffect } from 'react';
import { Download, Calendar, DollarSign, TrendingUp, Printer, Eye } from 'lucide-react';
import axios from 'axios';
import { generateOtherFeeReceipt, viewOtherFeeReceipt } from '@/utils/otherFeeReceiptGenerator';
import { fetchCollegeInfo } from '@/utils/receiptHeaderUtils';
import { useReportPagination } from '@/components/ReportPagination';
import { formatDate } from '../../utils/dateTimeUtils';

const PayOtherFeeReport = () => {
  const [payments, setPayments] = useState([]);
  const { paginatedData: pagePayments, PaginationControls } = useReportPagination(payments);
  const [filters, setFilters] = useState({ from_date: '', to_date: '' });
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, count: 0 });
  const [collegeInfo, setCollegeInfo] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    // Fetch fresh tenant-specific college info for receipts
    const loadCollegeInfo = async () => {
      if (token) {
        const info = await fetchCollegeInfo(API_URL, token);
        setCollegeInfo(info);
      }
    };
    loadCollegeInfo();
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      
      const response = await axios.get(`${API_URL}/api/fees/other-fee-payments?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const paymentsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setPayments(paymentsData);
      
      const total = paymentsData.reduce((sum, p) => sum + (p.amount || 0), 0);
      setStats({ total, count: paymentsData.length });
    } catch (error) {
      console.error('Error fetching other fee payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchPayments();
  };

  const handleExportCSV = () => {
    const csvContent = [
      ['Student Name', 'Student Number', 'Other Fee Name', 'Course', 'Session', 'Amount', 'Type', 'Description', 'Pay Date', 'Receipt Number', 'Payment Mode'],
      ...payments.map(p => [
        p.student_name || 'N/A',
        p.admission_number || 'N/A',
        p.other_fee_name || 'N/A',
        p.course || 'N/A',
        p.session || 'N/A',
        p.amount || 0,
        p.type || 'N/A',
        p.description || 'N/A',
        p.payment_date || 'N/A',
        p.receipt_number || 'N/A',
        p.payment_mode || 'N/A'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `other_fee_payments_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handleViewReceipt = async (payment) => {
    try {
      // Prepare receipt data
      const receiptData = {
        receipt_number: payment.receipt_number || 'N/A',
        payment_date: payment.payment_date || 'N/A',
        student_name: payment.student_name || 'N/A',
        admission_number: payment.admission_number || 'N/A',
        mobile: payment.mobile || 'N/A',
        email: payment.email || 'N/A',
        course: payment.course || 'N/A',
        session: payment.session || 'N/A',
        other_fee_name: payment.other_fee_name || 'N/A',
        semester: payment.semester || 'N/A',
        type: payment.type || 'N/A',
        amount: payment.amount || 0,
        fine_paid: payment.fine_paid || 0,
        payment_mode: payment.payment_mode || 'Cash',
        collected_by: payment.collected_by || 'Admin'
      };

      // View the PDF receipt in new tab with tenant-specific collegeInfo
      await viewOtherFeeReceipt(receiptData, collegeInfo);
    } catch (error) {
      console.error('Error viewing receipt:', error);
      alert('Failed to view receipt. Please try again.');
    }
  };

  const handlePrintReceipt = async (payment) => {
    try {
      // Prepare receipt data
      const receiptData = {
        receipt_number: payment.receipt_number || 'N/A',
        payment_date: payment.payment_date || 'N/A',
        student_name: payment.student_name || 'N/A',
        admission_number: payment.admission_number || 'N/A',
        mobile: payment.mobile || 'N/A',
        email: payment.email || 'N/A',
        course: payment.course || 'N/A',
        session: payment.session || 'N/A',
        other_fee_name: payment.other_fee_name || 'N/A',
        semester: payment.semester || 'N/A',
        type: payment.type || 'N/A',
        amount: payment.amount || 0,
        fine_paid: payment.fine_paid || 0,
        payment_mode: payment.payment_mode || 'Cash',
        collected_by: payment.collected_by || 'Admin'
      };

      // Generate the PDF receipt (downloads) with tenant-specific collegeInfo
      await generateOtherFeeReceipt(receiptData, collegeInfo);
    } catch (error) {
      console.error('Error generating receipt:', error);
      alert('Failed to generate receipt. Please try again.');
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Pay Other Fee Report</h1>
          <p className="text-gray-600 mt-2">View all other fee payment transactions</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Collection</p>
                <p className="text-2xl font-bold text-green-600">₹{stats.total.toLocaleString('en-IN')}</p>
              </div>
              <DollarSign className="text-green-600" size={32} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Payments</p>
                <p className="text-2xl font-bold text-blue-600">{stats.count}</p>
              </div>
              <TrendingUp className="text-blue-600" size={32} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Average Payment</p>
                <p className="text-2xl font-bold text-purple-600">
                  ₹{stats.count > 0 ? Math.round(stats.total / stats.count).toLocaleString('en-IN') : 0}
                </p>
              </div>
              <DollarSign className="text-purple-600" size={32} />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline mr-2" size={16} />
                From Date
              </label>
              <input
                type="date"
                value={filters.from_date}
                onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline mr-2" size={16} />
                To Date
              </label>
              <input
                type="date"
                value={filters.to_date}
                onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <button
              onClick={handleApplyFilters}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Apply Filters
            </button>

            <button
              onClick={handleExportCSV}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Download size={18} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Payments Table */}
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Number</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Other Fee Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fine Paid</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Mode</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt Number</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan="13" className="px-6 py-8 text-center text-gray-500">Loading...</td></tr>
              ) : payments.length === 0 ? (
                <tr><td colSpan="13" className="px-6 py-8 text-center text-gray-500">No payments found</td></tr>
              ) : (
                pagePayments.map((payment) => {
                  const finePaid = payment.fine_paid || 0;
                  const totalAmount = (payment.amount || 0) + finePaid;
                  
                  return (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{payment.student_name || 'N/A'}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{payment.admission_number || 'N/A'}</td>
                    <td className="px-4 py-4 text-sm font-semibold text-gray-800">{payment.other_fee_name || 'N/A'}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">{payment.course || 'N/A'}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">{payment.session || 'N/A'}</td>
                    <td className="px-4 py-4 text-sm font-semibold text-green-600">₹{(payment.amount || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-4 text-sm font-semibold text-red-600">₹{finePaid.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-4 text-sm font-bold text-blue-600">₹{totalAmount.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-4 text-sm">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        payment.type === 'Mandatory' || payment.type === 'mandatory'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {payment.type || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">{payment.payment_mode || 'N/A'}</td>
                    <td className="px-4 py-4 text-sm text-gray-500">{payment.payment_date ? formatDate(payment.payment_date) : 'N/A'}</td>
                    <td className="px-4 py-4 text-sm font-medium text-blue-600">{payment.receipt_number || 'N/A'}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewReceipt(payment)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                          title="View Receipt"
                        >
                          <Eye size={16} />
                          View
                        </button>
                        <button
                          onClick={() => handlePrintReceipt(payment)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                          title="Print Receipt"
                        >
                          <Printer size={16} />
                          Print
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <PaginationControls />
      </div>
    </div>
  );
};

export default PayOtherFeeReport;
